/**
 * 数字人链路耗时打点 — AI 科学圆桌专用
 *
 * 目的：把"按住说话 → 数字人开口"这段延迟拆成可比较的分段数字，
 * 用于回答"瓶颈到底在 ASR、群聊调度、TTS、LAM 还是浏览器渲染"。
 *
 * 两条独立时间线：
 *   turn  —— 一次观众提问：press → release → asr → send → queued → sound
 *   speak —— 一次角色发言：req → http → audio1 → bs1 → sound
 *            （角色接力时一次 turn 后会有多次 speak，所以必须分开统计）
 *
 * 同一时刻各只保留一条进行中的时间线。多数字人实例并发 speak 时后者覆盖前者，
 * 对"测首响"这个目的够用，不为并发精确性增加复杂度。
 *
 * 开关与展示复用 debugLog：Ctrl+Shift+D 打开调试面板即开始打点。
 * 控制台可用 window.expoPerf.report() / .csv() / .clear() 取数。
 */

import debugLog from "./debugLog.js";

const MAX_RECORDS = 100;

const TURN_STEPS = ["press", "release", "asr", "send", "queued", "sound"];
const SPEAK_STEPS = ["req", "http", "audio1", "bs1", "sound"];

// [显示名, 起点, 终点]
const TURN_SEGMENTS = [
  ["按住时长", "press", "release"],
  ["ASR识别", "release", "asr"],
  ["群聊调度", "send", "queued"],
  ["角色出声", "queued", "sound"],
];
const SPEAK_SEGMENTS = [
  ["HTTP应答", "req", "http"],
  ["TTS首包", "req", "audio1"],
  ["LAM首帧", "req", "bs1"],
  ["口型闸门", "audio1", "sound"],
];

const KINDS = {
  turn: { label: "提问链路", steps: TURN_STEPS, segments: TURN_SEGMENTS, from: "release", to: "sound" },
  speak: { label: "发言链路", steps: SPEAK_STEPS, segments: SPEAK_SEGMENTS, from: "req", to: "sound" },
};

const active = { turn: null, speak: null };
const records = [];

const fmt = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`);

const segmentsOf = (kind, marks) => {
  const out = {};
  for (const [label, from, to] of KINDS[kind].segments) {
    if (marks[from] != null && marks[to] != null) out[label] = marks[to] - marks[from];
  }
  return out;
};

const totalOf = (kind, marks) => {
  const { from, to } = KINDS[kind];
  return marks[from] != null && marks[to] != null ? marks[to] - marks[from] : null;
};

const finish = (kind) => {
  const cur = active[kind];
  if (!cur) return;
  active[kind] = null;

  const segments = segmentsOf(kind, cur.marks);
  const total = totalOf(kind, cur.marks);

  records.push({ kind, at: cur.at, note: cur.note, marks: { ...cur.marks }, segments, total });
  if (records.length > MAX_RECORDS) records.splice(0, records.length - MAX_RECORDS);

  const parts = Object.entries(segments).map(([k, v]) => `${k} ${fmt(v)}`);
  const head = `⏱️ ${KINDS[kind].label}${total != null ? ` 共 ${fmt(total)}` : ""}`;
  debugLog.emit("PERF", `${head}｜${parts.join(" · ")}${cur.note ? `｜${cur.note}` : ""}`);
};

const begin = (kind, note) => {
  if (!debugLog.isEnabled()) return;
  active[kind] = { at: Date.now(), note: note || "", marks: {} };
};

const mark = (kind, step, note) => {
  if (!debugLog.isEnabled()) return;
  const cur = active[kind];
  if (!cur) return;
  if (cur.marks[step] != null) return; // 只记首次，例如首个音频段
  cur.marks[step] = Date.now();
  if (note) cur.note = cur.note ? `${cur.note} ${note}` : note;
  if (step === KINDS[kind].to) finish(kind);
};

const percentile = (sorted, p) => {
  if (sorted.length === 0) return null;
  const idx = Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * p));
  return sorted[idx];
};

/** 按分段聚合已完成的记录，输出可直接贴进文档的文本表 */
const report = () => {
  if (records.length === 0) return "尚无耗时记录。打开调试面板（Ctrl+Shift+D）后走一遍完整交互再试。";

  const lines = [];
  for (const kind of Object.keys(KINDS)) {
    const rows = records.filter((r) => r.kind === kind);
    if (rows.length === 0) continue;

    lines.push(`【${KINDS[kind].label}】样本 ${rows.length} 次`);
    const cols = [...KINDS[kind].segments.map(([label]) => label), "合计"];
    for (const col of cols) {
      const values = rows
        .map((r) => (col === "合计" ? r.total : r.segments[col]))
        .filter((v) => v != null)
        .sort((a, b) => a - b);
      if (values.length === 0) continue;
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      lines.push(
        `  ${col.padEnd(6, "　")} 中位 ${fmt(percentile(values, 0.5))}` +
          `  均值 ${fmt(avg)}  最慢 ${fmt(values[values.length - 1])}  样本 ${values.length}`
      );
    }
    lines.push("");
  }
  return lines.join("\n");
};

/** 逐条明细，便于导入表格做进一步分析 */
const csv = () => {
  const header = ["kind", "at", "note", "total_ms"];
  const segCols = [];
  for (const kind of Object.keys(KINDS)) {
    for (const [label] of KINDS[kind].segments) {
      if (!segCols.includes(label)) segCols.push(label);
    }
  }
  const rows = records.map((r) => {
    const base = [r.kind, new Date(r.at).toISOString(), `"${(r.note || "").replace(/"/g, "'")}"`, r.total ?? ""];
    return [...base, ...segCols.map((c) => r.segments[c] ?? "")].join(",");
  });
  return [[...header, ...segCols].join(","), ...rows].join("\n");
};

export const latencyTrace = {
  beginTurn: (note) => begin("turn", note),
  markTurn: (step, note) => mark("turn", step, note),
  beginSpeak: (note) => begin("speak", note),
  markSpeak: (step, note) => mark("speak", step, note),

  /** 用户打断等场景：丢弃进行中的时间线，避免把等待时间算进下一次 */
  discard(kind, reason) {
    if (!active[kind]) return;
    active[kind] = null;
    if (reason) debugLog.emit("PERF", `⏱️ 丢弃${KINDS[kind].label}：${reason}`, "warn");
  },

  report,
  csv,
  snapshot: () => records.slice(),
  clear() {
    records.length = 0;
    active.turn = null;
    active.speak = null;
  },
};

if (typeof window !== "undefined") {
  window.expoPerf = latencyTrace;
}

export default latencyTrace;
